/**
 * Centralized API Configuration Module
 *
 * This module provides environment-aware API URL resolution:
 * - LOCAL DEVELOPMENT: Uses http://localhost:5001 (direct to backend)
 * - PRODUCTION (AWS EB): Uses relative URLs (same-origin, e.g., /api/...)
 *
 * Override Precedence (highest to lowest):
 * 1. REACT_APP_API_URL environment variable (if set at build time)
 * 2. REACT_APP_API_BASE_URL environment variable (if set at build time)
 * 3. Automatic hostname detection
 * 4. Fallback defaults
 *
 * IMPORTANT: This solution preserves existing localhost development workflow
 * while enabling production deployments to use same-origin relative URLs.
 */

// Environment detection helpers
const isLocalhost = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

/**
 * Get the API base URL for making API requests.
 *
 * Resolution logic:
 * 1. If REACT_APP_API_URL is set, use it (allows explicit override)
 * 2. If REACT_APP_API_BASE_URL is set, use it (legacy support)
 * 3. In development OR on localhost: use http://localhost:5001/api
 * 4. In production (non-localhost): use relative URL /api (same-origin)
 *
 * @returns The base URL for API requests (includes /api suffix)
 */
export const getApiBaseUrl = (): string => {
  // Priority 1: Explicit env var override
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Priority 2: Legacy env var support
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // Priority 3: Automatic detection based on environment
  // Use localhost in development mode OR when running on localhost
  // This ensures local testing works even in production build
  if (isDevelopment() || isLocalhost()) {
    return 'http://localhost:5001/api';
  }

  // Priority 4: Production - use relative URL (same-origin)
  // This allows the request to go to the same host serving the frontend
  return '/api';
};

/**
 * Get the backend base URL (without /api suffix).
 * Useful for loading static assets from the backend.
 *
 * @returns The backend server base URL
 */
export const getBackendBaseUrl = (): string => {
  // Priority 1: Explicit env var override
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  // Use localhost in development mode OR when running on localhost
  if (isDevelopment() || isLocalhost()) {
    return 'http://localhost:5001';
  }

  // Production - use empty string for same-origin requests
  return '';
};

/**
 * Build a full API URL from a relative path.
 * Handles both absolute and relative paths correctly.
 *
 * @param path - The API path (e.g., '/auth/login' or 'auth/login')
 * @returns Full URL for the API endpoint
 *
 * @example
 * // In development:
 * buildApiUrl('/auth/login') // => 'http://localhost:5001/api/auth/login'
 *
 * // In production:
 * buildApiUrl('/auth/login') // => '/api/auth/login'
 */
export const buildApiUrl = (path: string): string => {
  const baseUrl = getApiBaseUrl();

  // If path already starts with the baseUrl, return as-is
  if (path.startsWith(baseUrl)) {
    return path;
  }

  // If path starts with /api, we need to handle it carefully
  if (path.startsWith('/api/')) {
    // In production, baseUrl is '/api', so remove duplicate /api
    if (baseUrl === '/api') {
      return path;
    }
    // In development, baseUrl includes /api, so strip /api from path
    return `${baseUrl}${path.substring(4)}`;
  }

  // If path starts with http, it's absolute - return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // Normalize path to start with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return `${baseUrl}${normalizedPath}`;
};

/**
 * Get the full URL for an asset served by the backend.
 *
 * @param assetPath - The asset path (e.g., '/goran-lucic.jpg')
 * @returns Full URL for the asset
 */
export const getAssetUrl = (assetPath: string): string => {
  const backendUrl = getBackendBaseUrl();
  const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
  return `${backendUrl}${normalizedPath}`;
};

// Export environment detection helpers for components that need them
export { isLocalhost, isDevelopment, isProduction };

// Default export for convenience
export default {
  getApiBaseUrl,
  getBackendBaseUrl,
  buildApiUrl,
  getAssetUrl,
  isLocalhost,
  isDevelopment,
  isProduction,
};
